import { FC, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import { EventNotificationsResDto } from "service/api/estabrookSchemas";
import EventNotificationForm from "../components/event-notification-form";
import { NotificationTypeArray } from "service/types/enum/notifications-enum";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import { useEventNotificationForm } from "../hooks/useEventNotificationForm";
import DashboardContentLayout from "../../../pages/dashboard/components/dashboard-content-layout";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import { Button } from "@mui/material";

const EditEventNotificationPage: FC = () => {
  const { notificationId } = useParams<{
    notificationId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();

  const eventNotificationData = location.state?.eventNotificationData as
    | (EventNotificationsResDto & { notificationType?: NotificationTypeArray })
    | undefined;

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    setFieldValue,
    isLoading,
    resetForm,
  } = useEventNotificationForm({
    notificationId: notificationId!,
    eventNotificationData,
    onSuccess: () => {
      resetForm();
      setTimeout(() => {
        navigate(`${AdRoutes.notifications}?tab=routeNotifications`);
      }, 10);
    },
  });

  useEffect(() => {
    if (!eventNotificationData && notificationId) {
      navigate(`${AdRoutes.notifications}?tab=routeNotifications`);
    }
  }, [eventNotificationData, notificationId, navigate]);

  useUnsavedChangesBlocker(dirty);

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Notification Center",
                url: AdRoutes.notifications,
              },
              {
                label: "Edit Route Notification",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={submitForm}
              data-testid="save-event-notification-button"
            >
              Save Notification
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <EventNotificationForm
        values={values}
        handleChange={handleChange}
        handleBlur={handleBlur}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        deliveryText={
          eventNotificationData?.description ||
          "This notification is automatically sent when route events occur"
        }
        defaultMessage={eventNotificationData?.notificationDefaultMessage}
      />
    </DashboardContentLayout>
  );
};

export default EditEventNotificationPage;
